import React, { useCallback, useEffect, useRef } from 'react';

export interface ActiveItem {
  index: number;
  element: HTMLElement;
}

interface NavigationDelayProps {
  onItemActivated: (activeItem: ActiveItem) => void;
  tolerance?: number;
}

interface Coordinates {
  x: number;
  y: number;
}

const NUM_TRACKED_MOUSE_LOCATIONS = 3;

interface NavigationDelayHelpersProps {
  triggerProps: {
    onMouseEnterWrapper: (activeIndex: number) => React.MouseEventHandler;
    clearActiveRowTimeout: () => void;
  };
}

/**
 * The approach taken in the hook is heavily influenced by https://github.com/kamens/jQuery-menu-aim
 *
 * It can be used in a navigation type UI where a group of navigation items trigger menu contents on hover.
 *
 * Problems can arise when you navigate to the menu and pass over another navigation item or leave the
 * navigation container on the way to the menu.
 *
 * This hook takes a menu container ref and a callback and returns mouse handlers for the navigation container,
 * individual navigation items and menu and handles any necessary timeout logic.
 *
 */
export const useNavigationDelay = ({ onItemActivated }: NavigationDelayProps): NavigationDelayHelpersProps => {
  const mouseLocationsRef = useRef<Coordinates[]>([]);
  const activateTimerRef = useRef<number>();
  const clearActiveRowTimeout = useCallback(() => {
    if (activateTimerRef.current) {
      clearTimeout(activateTimerRef.current);
    }
  }, []);

  useEffect(
    () => () => {
      clearActiveRowTimeout();
    },
    [clearActiveRowTimeout]
  );

  const handleMouseMove = (e: MouseEvent) => {
    const x = e.clientX;
    const y = e.clientY;

    mouseLocationsRef.current.push({ x, y });
    if (mouseLocationsRef.current.length > NUM_TRACKED_MOUSE_LOCATIONS) {
      mouseLocationsRef.current.shift();
    }
  };

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const activateWithPossibleDelay = useCallback(
    (activeItem) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      onItemActivated(activeItem);
    },
    [onItemActivated]
  );

  const mouseEnterRow: (activeIndex: number) => React.MouseEventHandler = useCallback(
    (activeIndex) => (e: React.MouseEvent<HTMLInputElement>) => {
      const { currentTarget } = e;
      clearActiveRowTimeout();
      activateWithPossibleDelay({ index: activeIndex, element: currentTarget });
    },
    [activateWithPossibleDelay, clearActiveRowTimeout]
  );

  return {
    triggerProps: {
      onMouseEnterWrapper: mouseEnterRow,
      clearActiveRowTimeout,
    },
  };
};
